import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Tooltip } from '@eposnow/ui-core';
import { UIContext } from '../../../../context/UIContext';

const ActionsMenu = ({
  objectId,
  isLoading,
  handleDeleteClick,
  handleAddClick,
  handleDuplicateClick,
}: {
  objectId: string;
  isLoading: boolean;
  handleDeleteClick: (id: string) => void;
  handleAddClick: (id: string) => void;
  handleDuplicateClick: (id: string) => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { colorMode } = useContext(UIContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={isLoading ? t('actions.loading') : t('actions.openMenu')} theme={theme}>
        <IconButton
          onClick={handleMenuOpen}
          sx={{
            color: 'text.secondary',
            ':hover': {
              color: 'text.primary',
              backgroundColor: colorMode === 'light' ? 'grey.4p' : 'elevation.4dp',
            },
          }}
          aria-label={isLoading ? t('actions.loading') : t('actions.openMenu')}
          data-qa-id="three-dots-menu"
        >
          {isLoading ? (
            <CircularProgress color="inherit" size={24} />
          ) : (
            <MoreVertIcon fontSize="medium" />
          )}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleAddClick(objectId);
          }}
          data-qa-id="actions-add"
        >
          <ListItemIcon>
            <AddIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary={t('actions.add')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleDuplicateClick(objectId);
          }}
          data-qa-id="actions-duplicate"
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary={t('actions.duplicate')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleDeleteClick(objectId);
          }}
          data-qa-id="actions-delete"
        >
          <ListItemIcon>
            <DeleteIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary={t('actions.delete')} />
        </MenuItem>
      </Menu>
    </>
  );
};

export default ActionsMenu;
